import React, { useCallback, useMemo } from 'react';
import { StyleSheet, Pressable, View } from 'react-native';
import { useTheme } from '@/context/ThemeContext';
import Text from '@/components/Typography/Text';
import { useModalStore, useAuthStore } from '@/store';
import { PLUS_COLORS } from '@/constants/Colors';
import { PLUS_MONTHLY_CREDITS_DISPLAY } from '@/constants/config';
import { formatCreditBalance } from '@/lib/formatCredits';
import { availableCredits } from '@/lib/availableCredits';

const createStyles = (colors) => StyleSheet.create({
  upgradeButton: {
    borderWidth: 1,
    height: 30,
    paddingHorizontal: 12,
    borderRadius: 20,
    flexDirection: 'row',
    alignItems: 'center',
    alignSelf: 'center',
    gap: 6,
    backgroundColor: colors.solid_color_tertiary,
    borderColor: colors.solid_color_secondary,
  },
  upgradeText: {
    color: colors.text_color_secondary
  },
  upgradeLink: {
    height: 22,
    borderRadius: 12,
  },
  upgradeLinkText: {
    color: PLUS_COLORS.textColorPurple,
    lineHeight: 22,
  },
  buyCreditsLinkText: {
    color: colors.text_color_secondary,
    lineHeight: 22,
  },
});

function PageHeaderUpgradeButton() {
  const { colors } = useTheme();
  const { openModal } = useModalStore(state => state.actions);
  const totalAvailableCredits = useAuthStore(state => availableCredits(state.user?.credits));
  const reservedCredits = useAuthStore(state => state.user?.credits?.reservedCredits ?? 0);
  const hasUser = useAuthStore(state => Boolean(state.user?.id));
  const styles = useMemo(() => createStyles(colors), [colors]);
  const balanceKnown = Number.isFinite(totalAvailableCredits);
  const hasNoCredits = balanceKnown && totalAvailableCredits <= 0 && reservedCredits === 0;

  const handleSignIn = useCallback(() => {
    openModal('AuthCTAModal', {
      source: 'page_header_upgrade',
      title: 'Sign in to start creating',
      subtitle: 'Create a free account to chat, generate images, and use every tool.',
      icon: 'sparkles',
    });
  }, [openModal]);
  
  const handleUpgradePress = useCallback(() => {
    openModal('PricingModal', {
      title: "Unlock Work mode with Gab AI Plus",
      subtitle: hasNoCredits 
        ? `You've used all your credits. Plus unlocks Work mode, free default-model messages, and ${PLUS_MONTHLY_CREDITS_DISPLAY} monthly credits.`
        : "Give bigger jobs to Work mode, then let its AI agents plan, research, use tools, and deliver."
    });
  }, [openModal, hasNoCredits]);

  const handleBuyCreditsPress = useCallback(() => {
    // Always show pack selection first; never auto-buy a default pack.
    openModal('CreditsPurchaseModal', { initialTab: 'buy-credits' });
  }, [openModal]);

  const upgradeBtnText = hasNoCredits ? 'Upgrade Now' : 'Upgrade';
  const statusText = hasNoCredits 
    ? 'Out of credits' 
    : balanceKnown ? `${formatCreditBalance(totalAvailableCredits)} credits${reservedCredits > 0 ? ' available' : ''}` : '— credits';

  // Logged-out visitors have no credit balance — the credits/upgrade pill is
  // meaningless, so we surface a sign-in CTA instead.
  if (!hasUser) {
    return (
      <Pressable style={styles.upgradeButton} onPress={handleSignIn}>
        <Text weight='medium' style={styles.upgradeLinkText}>Sign in to start creating</Text>
      </Pressable>
    );
  }

  return (
    <View style={styles.upgradeButton}>
      <Text style={styles.upgradeText}>{statusText}</Text>
      {reservedCredits > 0 && <Text style={styles.upgradeText}>{formatCreditBalance(reservedCredits)} pending</Text>}
      <Text>·</Text>
      <Pressable style={styles.upgradeLink} onPress={handleUpgradePress}>
        <Text weight='medium' style={styles.upgradeLinkText}>{upgradeBtnText}</Text>
      </Pressable>
      <Text>·</Text>
      <Pressable style={styles.upgradeLink} onPress={handleBuyCreditsPress}>
        <Text weight='medium' style={styles.buyCreditsLinkText}>Buy Credits</Text>
      </Pressable>
    </View>
  );
};

export default React.memo(PageHeaderUpgradeButton);
